import { createOrder, verifyPayment, membershipVerify } from "@/lib/api";

function loadScript(src) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/**
 * Open Razorpay checkout for an ALREADY-CREATED order (from a booking endpoint).
 * `order` must contain: razorpay_order_id, razorpay_key_id, amount, currency, item_title, payment_ref.
 * On success, verifies server-side and returns { status:'paid', data } where data may include { booking }.
 */
export async function checkoutOrder(order, { name, email, phone }) {
  if (!order.payments_enabled) return { status: "not_configured", payment_ref: order.payment_ref };
  const ok = await loadScript(CHECKOUT_SRC);
  if (!ok) return { status: "failed", message: "Payment SDK failed to load." };

  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: order.razorpay_key_id,
      amount: order.amount,
      currency: order.currency,
      name: "YTCA \u2014 You Too Can Art",
      description: order.item_title,
      order_id: order.razorpay_order_id,
      prefill: { name, email, contact: phone || "" },
      theme: { color: "#17B6A6" },
      handler: async (res) => {
        try {
          const data = await verifyPayment({
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
            payment_ref: order.payment_ref,
          });
          resolve({ status: "paid", data });
        } catch (e) {
          resolve({ status: "failed", message: "Payment verification failed." });
        }
      },
      modal: { ondismiss: () => resolve({ status: "cancelled", message: "Payment cancelled." }) },
    });
    rzp.on("payment.failed", (resp) => resolve({ status: "failed", message: resp?.error?.description || "Payment failed." }));
    rzp.open();
  });
}

/**
 * Open Razorpay checkout for a subscription (membership).
 * `sub` must contain: razorpay_subscription_id, razorpay_key_id, amount, membership_ref.
 */
export async function checkoutSubscription(sub, { name, email, phone }) {
  const ok = await loadScript(CHECKOUT_SRC);
  if (!ok) return { status: "failed", message: "Payment SDK failed to load." };

  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: sub.razorpay_key_id,
      subscription_id: sub.razorpay_subscription_id,
      name: "YTCA \u2014 You Too Can Art",
      description: "YTCA Membership Pass (monthly)",
      prefill: { name, email, contact: phone || "" },
      theme: { color: "#17B6A6" },
      handler: async (res) => {
        try {
          const data = await membershipVerify({
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_subscription_id: res.razorpay_subscription_id,
            razorpay_signature: res.razorpay_signature,
            membership_ref: sub.membership_ref,
          });
          resolve({ status: "active", data });
        } catch (e) {
          resolve({ status: "failed", message: "Membership verification failed." });
        }
      },
      modal: { ondismiss: () => resolve({ status: "cancelled", message: "Cancelled." }) },
    });
    rzp.on("payment.failed", (resp) => resolve({ status: "failed", message: resp?.error?.description || "Payment failed." }));
    rzp.open();
  });
}


/**
 * Attempts a Razorpay payment.
 * Returns: { status: 'paid' | 'not_configured' | 'failed' | 'cancelled', message }
 */
export async function startPayment({ item_type, item_slug, name, email, phone }) {
  let order;
  try {
    order = await createOrder({ item_type, item_slug, name, email, phone });
  } catch (e) {
    return { status: "failed", message: e?.response?.data?.detail || "Could not start payment." };
  }

  if (!order.payments_enabled) {
    return { status: "not_configured", payment_ref: order.payment_ref };
  }

  const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
  if (!ok) return { status: "failed", message: "Payment SDK failed to load." };

  return new Promise((resolve) => {
    const options = {
      key: order.razorpay_key_id,
      amount: order.amount,
      currency: order.currency,
      name: "YTCA — You Too Can Art",
      description: order.item_title,
      order_id: order.razorpay_order_id,
      prefill: { name, email, contact: phone || "" },
      theme: { color: "#17B6A6" },
      handler: async (res) => {
        try {
          await verifyPayment({
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
            payment_ref: order.payment_ref,
          });
          resolve({ status: "paid", message: "Payment successful!" });
        } catch (e) {
          resolve({ status: "failed", message: "Payment verification failed." });
        }
      },
      modal: {
        ondismiss: () => resolve({ status: "cancelled", message: "Payment cancelled." }),
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => {
      resolve({ status: "failed", message: resp?.error?.description || "Payment failed. Please try again." });
    });
    rzp.open();
  });
}
